import type { MermaidElementModel } from '@blocksuite/affine-model';
import {
  GfxElementModelView,
  GfxViewInteractionExtension,
} from '@blocksuite/std/gfx';

import {
  clearMermaidVirtualSelection,
  enterMermaidVisualEditing,
} from './editor-state.js';
import { disposeMermaidCanvasState } from './renderer/element-renderer.js';
import { getMermaidInteractionSnapshot } from './renderer/state.js';

export class MermaidElementView extends GfxElementModelView<MermaidElementModel> {
  static override type = 'mermaid';

  override onCreated(): void {
    super.onCreated();
    this.on('click', evt => {
      const selected = this.gfx.selection.selectedElements;
      if (
        !this.gfx.selection.editing ||
        selected.length !== 1 ||
        selected[0] !== this.model ||
        this.std.store.readonly ||
        this.gfx.viewport.locked ||
        this.model.isLocked()
      ) {
        return;
      }

      const [x, y] = this.gfx.viewport.toModelCoord(evt.x, evt.y);
      void import('./renderer/interaction-geometry.js')
        .then(({ hitTestMermaidInteractionSnapshot }) => {
          const snapshot = getMermaidInteractionSnapshot(this.model);
          const hit = snapshot
            ? hitTestMermaidInteractionSnapshot(snapshot, this.model, [x, y])
            : null;
          enterMermaidVisualEditing(
            this.gfx,
            this.model,
            hit?.entityId ?? null,
            { inline: false }
          );
        })
        .catch(() => {
          enterMermaidVisualEditing(this.gfx, this.model, null, {
            inline: false,
          });
        });
    });

    this.on('dblclick', evt => {
      if (
        this.std.store.readonly ||
        this.gfx.viewport.locked ||
        this.model.isLocked()
      ) {
        return;
      }

      const [x, y] = this.gfx.viewport.toModelCoord(evt.x, evt.y);
      void import('./renderer/interaction-geometry.js')
        .then(({ hitTestMermaidInteractionSnapshot }) => {
          const snapshot = getMermaidInteractionSnapshot(this.model);
          const hit = snapshot
            ? hitTestMermaidInteractionSnapshot(snapshot, this.model, [x, y])
            : null;
          enterMermaidVisualEditing(
            this.gfx,
            this.model,
            hit?.entityId ?? null
          );
        })
        .catch(() => {
          enterMermaidVisualEditing(this.gfx, this.model, null);
        });
    });
  }

  override onDestroyed(): void {
    clearMermaidVirtualSelection(this.model);
    disposeMermaidCanvasState(this.model);
    super.onDestroyed();
  }
}

export const MermaidInteraction =
  GfxViewInteractionExtension<MermaidElementView>(MermaidElementView.type, {
    handleResize({ model }) {
      return {
        onResizeStart(context) {
          model.fitMode = 'free';
          context.default(context);
        },
        onResizeMove(context) {
          context.default(context);
        },
        onResizeEnd(context) {
          context.default(context);
        },
      };
    },
  });
